import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Plus, X, Award } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBadges } from '@/hooks/useBadges';
import { useCommunity } from '@/contexts/CommunityContext';
import { useAuth } from '@/contexts/AuthContext';
import { getIconComponent } from '@/utils/iconUtils';
import { Badge as BadgeType } from '@communitly/shared';

interface BadgeAssignmentProps {
  businessId: string;
  businessName: string;
  assignedBadges: BadgeType[];
  isListed: boolean;
  onAssignmentChange?: () => void;
}

export const BadgeAssignment: React.FC<BadgeAssignmentProps> = ({
  businessId,
  businessName,
  assignedBadges,
  isListed,
  onAssignmentChange
}) => {
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { data: allBadges, isLoading: badgesLoading } = useBadges(currentCommunity?.id || '');
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  // Filter available badges (active badges not already assigned)
  const assignedBadgeIds = assignedBadges.map(badge => badge.id);
  const availableBadges = allBadges?.filter(badge => 
    badge.is_active && !assignedBadgeIds.includes(badge.id)
  ) || [];

  // Filter badges based on search
  const filteredAvailableBadges = availableBadges.filter(badge =>
    badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (badge.description && badge.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Assign badge mutation
  const assignBadgeMutation = useMutation({
    mutationFn: async (badgeId: string) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('business_badges')
        .insert({
          business_id: businessId,
          badge_id: badgeId,
          assigned_at: new Date().toISOString(),
          assigned_by: user.id
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['business-badges'] });
      toast({
        title: "Success",
        description: "Badge assigned successfully",
      });
      onAssignmentChange?.();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to assign badge",
      });
    },
  });

  // Unassign badge mutation
  const unassignBadgeMutation = useMutation({
    mutationFn: async (badgeId: string) => {
      const { error } = await supabase
        .from('business_badges')
        .delete()
        .eq('business_id', businessId)
        .eq('badge_id', badgeId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['business-badges'] });
      toast({
        title: "Success",
        description: "Badge unassigned successfully",
      });
      onAssignmentChange?.();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unassign badge",
      });
    },
  });

  const handleAssignBadge = (badgeId: string) => {
    assignBadgeMutation.mutate(badgeId);
  };

  const handleUnassignBadge = (badgeId: string) => {
    unassignBadgeMutation.mutate(badgeId);
  };

  if (badgesLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Badge Assignment</CardTitle>
          <CardDescription>Loading badges...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Currently Assigned Badges */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Assigned Badges
          </CardTitle>
          <CardDescription>
            Badges currently assigned to {businessName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {assignedBadges.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No badges assigned</p>
              <p className="text-sm">Assign badges from the available list below</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assignedBadges.map((badge) => {
                const IconComponent = getIconComponent(badge.icon || 'Star');
                return (
                  <div key={badge.id} className="border rounded-lg p-3">
                    {/* Mobile-first layout matching Available Badges */}
                    <div className="space-y-3">
                      {/* Badge Header */}
                      <div className="flex items-center gap-3">
                        <div 
                          className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                          style={{ backgroundColor: badge.color + '20' }}
                        >
                          <IconComponent 
                            className="w-4 h-4"
                            style={{ color: badge.color }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {badge.translatedName || badge.name}
                          </div>
                        </div>
                      </div>

                      {/* Badge Description */}
                      <div className="text-sm text-muted-foreground pl-11">
                        {badge.description}
                      </div>

                      {/* Action Button */}
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnassignBadge(badge.id)}
                          disabled={unassignBadgeMutation.isPending}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Badges */}
      <Card>
        <CardHeader>
          <CardTitle>Available Badges</CardTitle>
          <CardDescription>
            Select badges to assign to {businessName}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search badges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Available Badges List */}
          <ScrollArea className="h-64">
            {filteredAvailableBadges.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {searchTerm ? 'No badges found matching your search' : 'All available badges are already assigned'}
                </p>
              </div>
            ) : (
              <div className="space-y-3 pr-4">
                {filteredAvailableBadges.map((badge) => {
                  const IconComponent = getIconComponent(badge.icon || 'Star');
                  return (
                    <div key={badge.id} className={`border rounded-lg p-3 ${badge.slug === 'featured' && !isListed ? 'opacity-60 bg-muted/30' : 'hover:bg-muted/50'}`}>
                      {/* Mobile-first layout */}
                      <div className="space-y-3">
                        {/* Badge Header */}
                        <div className="flex items-center gap-3">
                          <div 
                            className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                            style={{ backgroundColor: badge.color + '20' }}
                          >
                            <IconComponent 
                              className="w-4 h-4"
                              style={{ color: badge.color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {badge.translatedName || badge.name}
                            </div>
                          </div>
                        </div>

                        {/* Badge Description */}
                        <div className="text-sm text-muted-foreground pl-11">
                          {badge.description}
                        </div>

                        {/* Badges and Warnings */}
                        <div className="pl-11 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {badge.is_system_badge && (
                              <Badge variant="outline" className="text-xs">
                                System
                              </Badge>
                            )}
                            {badge.slug === 'featured' && !isListed && (
                              <Badge variant="destructive" className="text-xs">
                                Requires Listed Status
                              </Badge>
                            )}
                          </div>
                          {badge.slug === 'featured' && !isListed && (
                            <p className="text-xs text-amber-600">
                              Business must be listed to assign this badge
                            </p>
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleAssignBadge(badge.id)}
                            disabled={assignBadgeMutation.isPending || (badge.slug === 'featured' && !isListed)}
                            variant={badge.slug === 'featured' && !isListed ? 'secondary' : 'default'}
                            className={badge.slug === 'featured' && !isListed ? 'opacity-50 cursor-not-allowed' : ''}
                            title={badge.slug === 'featured' && !isListed ? 'Business must be listed to assign featured badge' : ''}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Assign
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};